/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { APP_INITIALIZER, EnvironmentProviders, inject, makeEnvironmentProviders } from '@angular/core';
import { OAuthModuleConfig, provideOAuthClient } from 'angular-oauth2-oidc';

import { EnvironmentService, resolveBackendApiUrl } from '../environment/environment.service';
import { AuthenticationService } from './authentication.service';

/**
 * Restricts the OAuth interceptor to the backend API so the access token is never sent to other hosts.
 * The interceptor matches by string prefix, so the allowed URL ends in '/' to stop e.g.
 * http://localhost:3000 from also matching http://localhost:30000.
 * @returns OAuth module config with the backend as the only resource server
 */
export function createOAuthModuleConfig(): OAuthModuleConfig {
  const backendApiUrl = resolveBackendApiUrl(inject(EnvironmentService).environment).replace(/\/+$/, '');
  return {
    resourceServer: {
      allowedUrls: [`${backendApiUrl}/`],
      sendAccessToken: true,
    },
  };
}

/**
 * App initializer that starts the authentication flow
 * @returns initializer function
 */
export function initAuth(): () => void {
  const authenticationService: AuthenticationService = inject(AuthenticationService);

  return () =>
    new Promise<void>((resolve) => {
      authenticationService.initialize().then((isAuthenticated) => {
        if (isAuthenticated) {
          resolve();
        }
      });
    });
}

/**
 * Provides the authentication service and sets up the app initializer
 * @returns environment providers for authentication
 */
export function provideAuthentication(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideOAuthClient(),
    { provide: OAuthModuleConfig, useFactory: createOAuthModuleConfig },
    AuthenticationService,
    {
      provide: APP_INITIALIZER,
      useFactory: initAuth,
      deps: [AuthenticationService],
      multi: true,
    },
  ]);
}
